import { useRef, useState, useCallback, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

interface UseItemsListVirtualizationOptions<TItem> {
  items: TItem[] | undefined;
  isDesktop: boolean;
  enableVirtualization?: boolean;
  batchSize?: number;
  estimatedItemHeight?: number;
  overscan?: number;
}

export const useItemsListVirtualization = <TItem,>({
  items,
  isDesktop,
  enableVirtualization = true,
  batchSize = 20,
  estimatedItemHeight = 60,
  overscan = 5,
}: UseItemsListVirtualizationOptions<TItem>) => {
  // Ref for the scrollable container
  const parentRef = useRef<HTMLDivElement>(null);

  // Virtualizer for desktop view - only renders visible items
  // Only create virtualizer if virtualization is enabled
  const virtualizer = useVirtualizer({
    count: enableVirtualization ? (items?.length ?? 0) : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimatedItemHeight,
    overscan,
    enabled: enableVirtualization,
  });

  // Mobile infinite scroll - load items in batches
  const [mobileItemsToShow, setMobileItemsToShow] = useState(batchSize);

  // Reset mobile items count when items change (e.g., search)
  useEffect(() => {
    setMobileItemsToShow(batchSize);
  }, [items?.length, batchSize]);

  const hasMoreMobileItems = (items?.length ?? 0) > mobileItemsToShow;

  const loadMoreMobileItems = useCallback(() => {
    if (hasMoreMobileItems) {
      setMobileItemsToShow((prev) =>
        Math.min(prev + batchSize, items?.length ?? 0)
      );
    }
  }, [hasMoreMobileItems, items?.length, batchSize]);

  // Scroll detection for mobile infinite scroll
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (isDesktop) return;

      const target = e.target as HTMLDivElement;
      const scrollBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      // Load more when user is within 100px of bottom
      if (scrollBottom < 100 && hasMoreMobileItems) {
        loadMoreMobileItems();
      }
    },
    [isDesktop, hasMoreMobileItems, loadMoreMobileItems]
  );

  // Get visible items for mobile (sliced by batch)
  const getMobileVisibleItems = useCallback(() => {
    return items?.slice(0, mobileItemsToShow) ?? [];
  }, [items, mobileItemsToShow]);

  return {
    parentRef,
    virtualizer,
    handleScroll,
    hasMoreMobileItems,
    getMobileVisibleItems,
    mobileItemsToShow,
    isVirtualizationEnabled: enableVirtualization && isDesktop,
  };
};
